import { client } from './amplifyClient';
import { findBestUserRowByEmail, pickUserRecordForSession, type LoginUserRow } from './resolveAppUser';

export type InvitationUserSeed = {
  inviteeRole?: string | null;
  organizationId?: string | null;
  kidProfileId?: string | null;
  email?: string | null;
};

/** One in-flight create per email — stops AuthContext + App.tsx racing duplicate rows. */
const createInflightByEmail = new Map<string, Promise<string | null>>();

async function getUserByCognitoId(cognitoUserId: string): Promise<LoginUserRow | null> {
  try {
    const res = await client.models.User.get({ id: cognitoUserId }, { authMode: 'apiKey' });
    return res.data ? (res.data as LoginUserRow) : null;
  } catch {
    try {
      const res = await client.models.User.get({ id: cognitoUserId }, { authMode: 'userPool' });
      return res.data ? (res.data as LoginUserRow) : null;
    } catch {
      return null;
    }
  }
}

/** Find an existing app User for this Cognito session — never creates. */
export async function resolveExistingAppUserId(
  cognitoUserId: string,
  emailRaw: string,
): Promise<string | null> {
  const email = emailRaw ? emailRaw.toLowerCase().trim() : '';
  const cognitoRow = await getUserByCognitoId(cognitoUserId);
  const emailRow = email ? await findBestUserRowByEmail(client, email) : null;
  const merged = pickUserRecordForSession(cognitoRow, emailRow);
  return merged?.id ?? null;
}

async function associateParentKidProfile(
  kidProfileId: string,
  parentUserId: string,
  organizationId?: string | null,
): Promise<void> {
  try {
    await client.models.KidProfile.update(
      {
        id: kidProfileId,
        parentId: parentUserId,
        organizationId: organizationId ?? undefined,
      } as Parameters<typeof client.models.KidProfile.update>[0],
      { authMode: 'apiKey' },
    );
  } catch {
    /* non-blocking */
  }
}

/**
 * Reuse an existing User row by email, or create exactly one row keyed to Cognito sub.
 * Call this instead of raw User.create from invitation / post-login bootstrap paths.
 */
export async function ensureAppUserFromInvitation(params: {
  cognitoUserId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  invitation: InvitationUserSeed;
  passwordMarker: 'email_auth_user' | 'google_auth_user';
}): Promise<string | null> {
  const email = params.email.toLowerCase().trim();
  if (!email) return null;

  const existing = await resolveExistingAppUserId(params.cognitoUserId, email);
  if (existing) {
    if (params.invitation.inviteeRole === 'PARENT' && params.invitation.kidProfileId) {
      await associateParentKidProfile(
        params.invitation.kidProfileId,
        existing,
        params.invitation.organizationId,
      );
    }
    return existing;
  }

  let inflight = createInflightByEmail.get(email);
  if (!inflight) {
    inflight = (async () => {
      try {
        const emailRow = await findBestUserRowByEmail(client, email);
        if (emailRow?.id) {
          if (params.invitation.inviteeRole === 'PARENT' && params.invitation.kidProfileId) {
            await associateParentKidProfile(
              params.invitation.kidProfileId,
              emailRow.id,
              params.invitation.organizationId,
            );
          }
          return emailRow.id;
        }

        const username = email.split('@')[0];
        const userData = {
          id: params.cognitoUserId,
          email,
          fName: params.firstName || params.invitation.email?.split('@')[0] || username || 'User',
          lName: params.lastName || '',
          username,
          role: params.invitation.inviteeRole,
          organizationId: params.invitation.organizationId,
          status: 'ACTIVE',
          address: 'Not provided',
          dob: new Date().toISOString().split('T')[0],
          mName: '-',
          password: params.passwordMarker,
          phoneNumber: '',
        };

        const created = await client.models.User.create(userData, { authMode: 'apiKey' });
        const newId = created.data?.id ?? null;
        if (!newId) return null;

        if (params.invitation.inviteeRole === 'PARENT' && params.invitation.kidProfileId) {
          await associateParentKidProfile(
            params.invitation.kidProfileId,
            newId,
            params.invitation.organizationId,
          );
        }
        return newId;
      } finally {
        createInflightByEmail.delete(email);
      }
    })();
    createInflightByEmail.set(email, inflight);
  }

  return inflight;
}

/** Ensure a PARENT User row exists for this Cognito session (parent onboarding / kid wizard). */
export async function ensureParentAppUser(params: {
  cognitoUserId: string;
  email: string;
  firstName?: string;
  lastName?: string;
}): Promise<string | null> {
  return ensureAppUserFromInvitation({
    cognitoUserId: params.cognitoUserId,
    email: params.email,
    firstName: params.firstName,
    lastName: params.lastName,
    invitation: { inviteeRole: 'PARENT' },
    passwordMarker: 'email_auth_user',
  });
}
